import { rankItem } from '@tanstack/match-sorter-utils';
import { FilterFn, FilterMeta, Row } from '@tanstack/react-table';
import * as changeCase from 'change-case';
import { ReactNode } from 'react';

import { removeCommas } from '../../utils';
import { Tooltip } from '../Tooltip/Tooltip';

import type { RowData } from './table-types';

export type ModularColumnDef<
  T extends RowData,
  K extends string = keyof T & string,
> = {
  id: K;
  accessorKey: K;
  label: string;
  header: () => ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cell: (info: { getValue: () => any }) => ReactNode;
  size?: number;
};

export type ModularTableDef<T extends RowData, Params> = {
  title: string;
  columns: ModularColumnDef<T, string>[];
  loader: (params: Params) => Promise<T[]>;
};

type ModularTableDefInput<T extends RowData, Params> = {
  title: string;
  columns: readonly ModularColumnDef<T, string>[];
  loader: (params: Params) => Promise<T[]>;
};

type ColumnOpts = {
  label?: string;
  width?: 's' | 'm' | 'l';
};

export function defineTable<T extends RowData, Params>({
  title,
  columns,
  loader,
}: ModularTableDefInput<T, Params>): ModularTableDef<T, Params> {
  return {
    title,
    columns: [...columns],
    loader,
  };
}

export function defineColumn<T extends RowData, K extends keyof T & string>(
  columnId: K,
  {
    label,
    renderValue,
    width,
  }: ColumnOpts & {
    renderValue?: (value: T[K]) => React.ReactNode;
  },
): ModularColumnDef<T, K> {
  label = label ?? changeCase.sentenceCase(columnId);
  renderValue = renderValue ?? ((value) => value?.toLocaleString());
  return {
    id: columnId,
    accessorKey: columnId,
    label,
    header: () => renderHeaderTooltip(label),
    cell: (info) => renderValue(info.getValue() as T[K]),
    size: width
      ? {
          s: 75,
          m: 150,
          l: 300,
        }[width]
      : undefined,
  };
}

export function defineTextColumn<
  T extends RowData,
  K extends keyof T & string = keyof T & string,
>(columnId: K, opts: ColumnOpts = {}): ModularColumnDef<T, K> {
  return defineColumn(columnId, {
    ...opts,
    renderValue: (value) => (
      <div style={{ textAlign: 'left' }}>{value?.toString()}</div>
    ),
  });
}

export function defineNumericColumn<
  T extends RowData,
  K extends keyof T & string = keyof T & string,
>(columnId: K, opts: ColumnOpts = {}): ModularColumnDef<T, K> {
  return defineColumn(columnId, {
    ...opts,
    renderValue: (value) => (
      <div style={{ textAlign: 'right' }}>{value?.toLocaleString()}</div>
    ),
  });
}

export function fuzzyFilter<TData>(
  row: Row<TData>,
  _columnId: string,
  value: unknown,
  addMeta: (meta: FilterMeta) => void,
): boolean {
  // This allows searching numbers with comma separators
  const stringValue = String(value);
  const newValue: string = Number(removeCommas(stringValue))
    ? removeCommas(stringValue)
    : stringValue;
  const searchTerms = newValue.split(' ');

  // Check if every search term is present in any cell in the row
  const termMatches = searchTerms.every((term) =>
    row
      .getVisibleCells()
      .filter(
        (item) =>
          item.column.columnDef.enableGlobalFilter ||
          item.column.getCanGlobalFilter(),
      )
      .some((cell) => rankItem(cell.getValue(), term, { threshold: 3 }).passed),
  );

  // Store the result
  addMeta({
    termMatches,
  });

  return termMatches;
}

fuzzyFilter satisfies FilterFn<RowData>;

export function getPageSizeOptions(
  pageSizeOptions: number[],
  totalEntries: number,
  manualPagination: boolean,
): number[] {
  return pageSizeOptions.filter(
    (key, i) =>
      key < totalEntries ||
      (!manualPagination && (pageSizeOptions[i - 1] ?? 0) < totalEntries),
  );
}

export function getShowingRange(
  page: number,
  pageSize: number,
  totalEntries: number,
  hasPageSizeOptions: boolean,
): { from: number; to: number } {
  return {
    from:
      pageSize * (page - 1) > 0
        ? pageSize * (page - 1) + 1
        : hasPageSizeOptions
          ? 1
          : totalEntries > 0
            ? 1
            : 0,
    to: pageSize * page > totalEntries ? totalEntries : pageSize * page,
  };
}

function renderHeaderTooltip(value: string): ReactNode {
  return (
    <Tooltip title={value} arrow={false}>
      <span>{value}</span>
    </Tooltip>
  );
}
